import React, { useState, useEffect } from 'react';
import { Row, Col, Spinner, ButtonGroup, Button, Form, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTicketAlt,
    faUsers,
    faClock,
    faCheckCircle,
    faChartLine,
    faUserClock,
    faTrophy,
    faCalendarDay,
    faSearch,
    faDownload,
    faHistory
} from '@fortawesome/free-solid-svg-icons';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import request from '../../../../request';

/**
 * AdminStats - Premium Dashboard with Real-time Data and Historical Analytics
 */
const AdminStats = ({ ipserv, branch_id }) => {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({
        totalToday: 0,
        waiting: 0,
        attendedToday: 0,
        activeStaff: 0
    });
    const [activity, setActivity] = useState([]);
    const [rankingPeriod, setRankingPeriod] = useState('day');
    const [ranking, setRanking] = useState([]);

    // Historical State
    const todayStr = new Date().toISOString().split('T')[0];
    const sevenDaysAgoStr = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(sevenDaysAgoStr);
    const [endDate, setEndDate] = useState(todayStr);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyStats, setHistoryStats] = useState({ total: 0, attended: 0, avgTime: 0 });
    const [historyList, setHistoryList] = useState([]);

    useEffect(() => {
        if (branch_id) {
            fetchDashboardData();
            fetchHistory();
        }
    }, [branch_id]);

    useEffect(() => {
        if (branch_id) {
            fetchRanking();
        }
    }, [branch_id, rankingPeriod]);

    const fetchDashboardData = async () => {
        setLoading(true);
        request.Get(ipserv + 'dashboard-summary', { sucursalId: branch_id }, r => {
            if (r.success && r.data.length > 0) {
                setSummary(r.data[0]);
            }
        });

        request.Get(ipserv + 'dashboard-activity', { sucursalId: branch_id }, r => {
            if (r.success) {
                const formatted = (r.data || []).map(item => ({
                    name: item.day.split('-').slice(2).join('/'),
                    turnos: item.count
                }));
                setActivity(formatted);
            }
            setLoading(false);
        });
    };

    const fetchRanking = () => {
        request.Get(ipserv + 'dashboard-ranking', { sucursalId: branch_id, period: rankingPeriod }, r => {
            if (r.success) {
                setRanking(r.data || []);
            }
        });
    };

    const fetchHistory = () => {
        setHistoryLoading(true);
        request.Get(ipserv + 'dashboard-history', {
            sucursalId: branch_id,
            startDate: startDate,
            endDate: endDate
        }, r => {
            if (r.success) {
                setHistoryStats(r.stats);
                setHistoryList(r.list);
            }
            setHistoryLoading(false);
        });
    };

    const exportToCSV = () => {
        if (historyList.length === 0) return;

        const headers = ["ID", "Numero", "Cliente", "Estado", "Fecha Alta", "Atendio", "Fecha Fin"];
        const rows = historyList.map(t => [
            t.TurnoId,
            t.TurnoNumero,
            t.TurnoNombreCliente,
            t.TurnoEstado,
            t.TurnoFchAlta,
            `${t.UsrNombre || ''} ${t.UsrApellido || ''}`,
            t.TurnoFchFin || ''
        ]);

        let csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `reporte_tufila_${startDate}_${endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const stats = [
        { title: 'Turnos Hoy', value: summary.totalToday, icon: faTicketAlt, variant: 'stats-info', label: 'Registrados hoy' },
        { title: 'En Espera', value: summary.waiting, icon: faClock, variant: 'stats-warning', label: 'Pendientes' },
        { title: 'Atendidos', value: summary.attendedToday, icon: faCheckCircle, variant: 'stats-success', label: 'Efectuados hoy' },
        { title: 'Staff Activo', value: summary.activeStaff, icon: faUsers, variant: 'stats-purple', label: 'Operadores' }
    ];

    if (!branch_id) {
        return (
            <div className="text-center py-5 text-muted animate-in">
                <FontAwesomeIcon icon={faChartLine} size="3x" className="mb-3 opacity-20" />
                <h5>Selecciona una sucursal para ver las métricas.</h5>
            </div>
        );
    }

    return (
        <div className="animate-in pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 style={{ fontWeight: 800, marginBottom: '0.25rem' }}>Resumen del Sistema</h2>
                    <p className="text-muted small">Panel de control y métricas operativas en tiempo real.</p>
                </div>
                {loading && <Spinner animation="border" size="sm" variant="primary" />}
            </div>

            <div className="stats-grid mb-4">
                {stats.map((stat, idx) => (
                    <div key={idx} className={`stats-card ${stat.variant}`}>
                        <div className="stats-icon-box">
                            <FontAwesomeIcon icon={stat.icon} />
                        </div>
                        <div className="stats-info">
                            <p>{stat.title}</p>
                            <h3>{stat.value}</h3>
                            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>{stat.label}</small>
                        </div>
                    </div>
                ))}
            </div>

            <Row>
                <Col lg={8} className="mb-4">
                    <div className="turnero-card p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="m-0 font-weight-bold">
                                <FontAwesomeIcon icon={faChartLine} className="mr-2 text-primary" />
                                Actividad de la Semana
                            </h5>
                            <span className="badge badge-primary-soft text-primary small">Últimos 7 días</span>
                        </div>

                        <div style={{ height: '320px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={activity}>
                                    <defs>
                                        <linearGradient id="colorTurns" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px' }} itemStyle={{ color: '#fff' }} />
                                    <Area type="monotone" dataKey="turnos" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorTurns)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </Col>

                <Col lg={4} className="mb-4">
                    <div className="turnero-card p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="m-0 font-weight-bold">
                                <FontAwesomeIcon icon={faTrophy} className="mr-2 text-warning" /> Ranking Staff
                            </h5>
                            <ButtonGroup size="sm">
                                <Button variant={rankingPeriod === 'day' ? 'primary' : 'outline-secondary'} onClick={() => setRankingPeriod('day')} className="border-0" style={{ fontSize: '0.7rem' }}>Día</Button>
                                <Button variant={rankingPeriod === 'week' ? 'primary' : 'outline-secondary'} onClick={() => setRankingPeriod('week')} className="border-0" style={{ fontSize: '0.7rem' }}>Semana</Button>
                            </ButtonGroup>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            {ranking.length === 0 ? (
                                <div className="text-center py-5 text-muted small">No hay datos registrados aún.</div>
                            ) : (
                                ranking.map((user, idx) => (
                                    <div key={idx} className="p-2 rounded hover-bg-glass transition-all">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="d-flex align-items-center justify-content-center rounded-circle small font-weight-bold" style={{ width: '24px', height: '24px', background: idx === 0 ? '#fbbf24' : 'rgba(255,255,255,0.1)', color: idx === 0 ? '#000' : '#fff' }}>{idx + 1}</div>
                                                <span className="small font-weight-bold">{user.UsrNombre} {user.UsrApellido}</span>
                                            </div>
                                            <span className="badge badge-glass">{user.totalTurns} turnos</span>
                                        </div>
                                        <div className="progress" style={{ height: '4px', background: 'rgba(255,255,255,0.05)' }}>
                                            <div className="progress-bar bg-primary" style={{ width: `${(user.totalTurns / (ranking[0]?.totalTurns || 1)) * 100}%` }}></div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {/* SECCION HISTORIAL */}
            <div className="turnero-card p-4 mt-2">
                <div className="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4 pb-3 border-bottom border-light border-opacity-10">
                    <div>
                        <h4 style={{ fontWeight: 800, marginBottom: '0.25rem' }}>
                            <FontAwesomeIcon icon={faHistory} className="mr-2 text-info" /> Análisis Histórico
                        </h4>
                        <p className="text-muted small m-0">Consulta y exporta la actividad de períodos anteriores.</p>
                    </div>

                    <div className="d-flex gap-2 align-items-center">
                        <Form.Group className="mb-0">
                            <Form.Label className="tiny text-muted mb-1">Desde</Form.Label>
                            <Form.Control type="date" size="sm" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-dark border-secondary text-white" />
                        </Form.Group>
                        <Form.Group className="mb-0">
                            <Form.Label className="tiny text-muted mb-1">Hasta</Form.Label>
                            <Form.Control type="date" size="sm" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-dark border-secondary text-white" />
                        </Form.Group>
                        <Button variant="primary" size="sm" className="mt-4" onClick={fetchHistory} disabled={historyLoading}>
                            <FontAwesomeIcon icon={faSearch} />
                        </Button>
                        <Button variant="success" size="sm" className="mt-4" onClick={exportToCSV} disabled={historyList.length === 0}>
                            <FontAwesomeIcon icon={faDownload} />
                        </Button>
                    </div>
                </div>

                {historyLoading ? (
                    <div className="text-center py-5">
                        <Spinner animation="grow" variant="info" />
                        <p className="mt-3 text-muted small">Buscando en los registros...</p>
                    </div>
                ) : (
                    <>
                        <Row className="mb-4">
                            <Col md={4} className="mb-3">
                                <div className="p-3 rounded border border-light border-opacity-10 bg-glass d-flex align-items-center gap-3">
                                    <div className="text-info opacity-50"><FontAwesomeIcon icon={faTicketAlt} size="2x" /></div>
                                    <div>
                                        <p className="text-muted tiny m-0">Total Período</p>
                                        <h5 className="m-0 font-weight-bold">{historyStats.total} <small className="text-muted small">turnos</small></h5>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="mb-3">
                                <div className="p-3 rounded border border-light border-opacity-10 bg-glass d-flex align-items-center gap-3">
                                    <div className="text-success opacity-50"><FontAwesomeIcon icon={faCheckCircle} size="2x" /></div>
                                    <div>
                                        <p className="text-muted tiny m-0">Atendidos</p>
                                        <h5 className="m-0 font-weight-bold">{historyStats.attended} <small className="text-muted small">total</small></h5>
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="mb-3">
                                <div className="p-3 rounded border border-light border-opacity-10 bg-glass d-flex align-items-center gap-3">
                                    <div className="text-warning opacity-50"><FontAwesomeIcon icon={faClock} size="2x" /></div>
                                    <div>
                                        <p className="text-muted tiny m-0">Promedio Atención</p>
                                        <h5 className="m-0 font-weight-bold">{Math.round(historyStats.avgTime)} <small className="text-muted small">minutos</small></h5>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table className="table-dark table-hover m-0" style={{ fontSize: '0.85rem' }}>
                                <thead className="bg-glass">
                                    <tr>
                                        <th className="border-0">#</th>
                                        <th className="border-0">Cliente</th>
                                        <th className="border-0">Estado</th>
                                        <th className="border-0">Alta</th>
                                        <th className="border-0">Atendió</th>
                                        <th className="border-0">Finalización</th>
                                        <th className="border-0">Tiempo de Atención</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyList.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5 text-muted">No se encontraron resultados para este período.</td>
                                        </tr>
                                    ) : (
                                        historyList.map((t, idx) => (
                                            <tr key={idx} className="transition-all">
                                                <td className="font-weight-bold text-primary">{t.TurnoNumero}</td>
                                                <td>{t.TurnoNombreCliente}</td>
                                                <td><span className={`badge badge-${t.TurnoEstado === 'FINALIZADO' ? 'success' : t.TurnoEstado === 'DERIVADO' ? 'info' : 'warning'}-soft`}>{t.TurnoEstado}</span></td>
                                                <td className="text-muted">{new Date(t.TurnoFchAlta).toLocaleString()}</td>
                                                <td>{t.UsrNombre ? `${t.UsrNombre} ${t.UsrApellido}` : '-'}</td>
                                                <td className="text-muted">{t.TurnoFchFin ? new Date(t.TurnoFchFin).toLocaleString() : '-'}</td>
                                                <td className="text-muted">{t.TiempoAtencion ? t.TiempoAtencion : '-'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminStats;
